import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { collection, query, where, getDocs, addDoc, serverTimestamp, updateDoc, doc } from 'firebase/firestore';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import OrderForm from '../components/OrderForm';
import { Helmet } from 'react-helmet-async';
import { Check, ShoppingBag } from 'lucide-react';
import ProcessSection from '../components/ProcessSection';
import ImageGuidelinesSection from '../components/ImageGuidelinesSection';
import { trackMetaEvent, formatProductData } from '../utils/metaPixel';
import { useDesignSession } from '../contexts/DesignSessionContext';

const WHATSAPP_NUMBER = "94771234567";

// Hero Image Carousel Component
const HeroCarousel = ({ images }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        if (images.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % images.length);
        }, 3000);

        return () => clearInterval(interval);
    }, [images.length]);

    const goToPrev = () => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    const goToNext = () => setCurrentIndex((prev) => (prev + 1) % images.length);

    if (images.length === 0) return null;

    return (
        <div
            className="relative w-full aspect-[3/4] max-w-md mx-auto group"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Image Container */}
            <div className="relative w-full h-full rounded-2xl overflow-hidden shadow-2xl">
                {images.map((img, idx) => (
                    <div
                        key={idx}
                        className={`absolute inset-0 transition-opacity duration-700 ${idx === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
                    >
                        <img
                            src={img.url}
                            alt={img.alt || `Hero ${idx + 1}`}
                            className="w-full h-full object-cover"
                            loading={idx === 0 ? "eager" : "lazy"}
                        />
                    </div>
                ))}

                {/* Navigation Arrows - Desktop hover only */}
                {images.length > 1 && (
                    <>
                        <button
                            onClick={goToPrev}
                            className={`absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
                            aria-label="Previous image"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <button
                            onClick={goToNext}
                            className={`absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-black/40 hover:bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}
                            aria-label="Next image"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </>
                )}

                {/* Navigation Dots - Higher position with subtle backdrop */}
                {images.length > 1 && (
                    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2.5 bg-black/25 backdrop-blur-sm px-3 py-2 rounded-full">
                        {images.map((_, idx) => (
                            <button
                                key={idx}
                                onClick={() => setCurrentIndex(idx)}
                                className={`rounded-full transition-all duration-300 ${idx === currentIndex
                                    ? 'bg-white w-7 h-2.5 shadow-lg'
                                    : 'bg-white/60 w-2.5 h-2.5 hover:bg-white'
                                    }`}
                                aria-label={`Go to image ${idx + 1}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// Icon mapping for specs
const getSpecIcon = (iconName) => {
    const icons = {
        'material': (
            <svg className="w-8 h-8 text-[#E85D44]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
        ),
        'clock': (
            <svg className="w-8 h-8 text-[#E85D44]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
        'light': (
            <svg className="w-8 h-8 text-[#E85D44]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
        ),
        'shield': (
            <svg className="w-8 h-8 text-[#E85D44]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
        ),
        'star': (
            <svg className="w-8 h-8 text-[#E85D44]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
        )
    };

    return icons[iconName] || icons['star'];
};

const ProductPage = () => {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { initSession, designData, sessionId } = useDesignSession();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showOrderPopup, setShowOrderPopup] = useState(false);

    // Check for ?order=true in URL to auto-open order popup
    useEffect(() => {
        if (searchParams.get('order') === 'true' && product && !loading) {
            setShowOrderPopup(true);
        }
    }, [searchParams, product, loading]);

    useEffect(() => {
        const fetchProduct = async () => {
            setLoading(true);
            try {
                const q = query(collection(db, 'products'), where('slug', '==', slug), where('status', '==', 'published'));
                const snapshot = await getDocs(q);
                if (!snapshot.empty) {
                    const data = snapshot.docs[0].data();
                    const productData = { id: snapshot.docs[0].id, ...data };
                    setProduct(productData);

                    // Track ViewContent event
                    trackMetaEvent('ViewContent', formatProductData(productData, 1));
                } else {
                    setProduct(null);
                }
            } catch (error) {
                console.error('Error fetching product:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProduct();
    }, [slug]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-white px-4">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">Product Not Found</h1>
                <p className="text-gray-600 mb-8">The product you are looking for does not exist or has been removed.</p>
                <Link to="/" className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors">
                    Back to Home
                </Link>
            </div>
        );
    }

    const hero = product.hero || {};
    const specs = product.specs || [];
    const contentSections = product.contentSections || [];
    const showProcess = product.showProcess !== false;
    const showGuidelines = product.showGuidelines !== false;

    // Discount Logic
    const paymentOptions = product.paymentOptions || {};
    const discount = paymentOptions.discount || {};
    let isDiscountActive = discount.enabled && discount.amount > 0;

    if (isDiscountActive && discount.deadline) {
        // Handle Firestore Timestamp or Date string/object
        const deadline = discount.deadline.seconds
            ? new Date(discount.deadline.seconds * 1000)
            : new Date(discount.deadline);
        if (new Date() > deadline) {
            isDiscountActive = false;
        }
    }

    // Base price from product, NOT from payment options
    const basePrice = product.price || 0;
    const effectivePrice = isDiscountActive ? Math.max(0, basePrice - discount.amount) : basePrice;

    const deliveryText = hero.freeDelivery
        ? '+ Free Delivery'
        : hero.deliveryPrice ? `+ ${product.priceCurrency} ${hero.deliveryPrice} Delivery` : '';

    const whatsappMessage = encodeURIComponent(`Hi! I'd like to talk to you about ${product.name}.`);
    const whatsappDesignMessage = encodeURIComponent(`Hi! I'd like to talk to you about designing my ${product.name}.`);

    // Handle "Order Now" button click - check for custom design page
    const handleOrderNowClick = () => {
        const customDesign = product.customDesignPage || {};

        if (customDesign.enabled && customDesign.position === 'before') {
            // Initialize design session and navigate to design page
            initSession(product);
            navigate(`/products/${slug}/design`);
        } else {
            // Show order popup directly
            setShowOrderPopup(true);
        }
    };

    const handleOrderSubmit = async (orderData) => {
        try {
            const finalOrder = {
                ...orderData,
                productId: product.id,
                productName: product.name,
                productSlug: product.slug,
                productImage: product.primaryImage?.url || null,
                status: 'SUBMITTED',
                createdAt: serverTimestamp(),
                statusHistory: {
                    SUBMITTED: {
                        completedAt: new Date()
                    }
                }
            };

            const docRef = await addDoc(collection(db, 'orders'), finalOrder);

            // Handle Custom Design Data Upload
            if (designData && designData.imageUrl) {
                try {
                    // Upload the base64 image
                    const imageRef = ref(storage, `orders/${docRef.id}/custom_design_${Date.now()}.png`);
                    await uploadString(imageRef, designData.imageUrl, 'data_url');
                    const downloadUrl = await getDownloadURL(imageRef);

                    // Create image object for template.images
                    // Create image object for template.images
                    const designImage = {
                        url: downloadUrl,
                        fileName: 'Custom Design Upload',
                        uploadedAt: new Date().toISOString()
                    };

                    // Sanitize designData: remove File objects and replace base64 with storage URL
                    const { imageFile, imageUrl, ...otherDesignParts } = designData;
                    const sanitizedDesignData = {
                        ...otherDesignParts,
                        imageUrl: downloadUrl // Replace base64 with the uploaded URL
                    };

                    // Construct updates
                    const updates = {
                        'design': {
                            sessionId: sessionId,
                            type: designData.type,
                            data: sanitizedDesignData,
                            previewUrl: downloadUrl // Using the uploaded raw image as preview/asset
                        }
                    };

                    // Check if template exists in the original order object
                    if (finalOrder.template) {
                        // Template exists, safe to use dot notation for images
                        const currentImages = finalOrder.template.images || [];
                        updates['template.images'] = [...currentImages, designImage];
                    } else {
                        // Template doesn't exist, must create the entire map
                        updates['template'] = {
                            name: 'Custom Design',
                            quantity: finalOrder.priceAtOrder?.quantity || 1,
                            images: [designImage],
                        };
                    }

                    await updateDoc(doc(db, 'orders', docRef.id), updates);

                } catch (designError) {
                    console.error("Error uploading custom design:", designError);
                    // Don't block success popup, but maybe log it
                }
            }

            // Delay closing OrderPopup until user dismisses or acts on Swal

            // Check if payment options are enabled for this product
            const paymentOptions = product.paymentOptions || {};
            const paymentEnabled = paymentOptions.enabled || false;

            if (paymentEnabled && (paymentOptions.showPayButton || paymentOptions.showSkipButton)) {
                // Show payment options dialog
                // Use order's priceAtOrder for accurate totals (includes quantity)
                const orderPricing = orderData.priceAtOrder || {};
                const quantity = orderPricing.quantity || 1;
                const unitPrice = orderPricing.unitPrice || product.price || 0;
                const currency = orderPricing.currency || product.priceCurrency || 'LKR';

                // Calculate totals based on quantity
                const baseAmount = unitPrice * quantity;

                // Check for discount from order data or product payment options
                const discount = paymentOptions.discount || {};
                let isDiscountActive = discount.enabled && discount.amount > 0;

                if (isDiscountActive && discount.deadline) {
                    const deadline = discount.deadline.seconds
                        ? new Date(discount.deadline.seconds * 1000)
                        : new Date(discount.deadline);
                    if (new Date() > deadline) {
                        isDiscountActive = false;
                    }
                }

                // Apply discount per unit then multiply by quantity
                const discountPerUnit = isDiscountActive ? discount.amount : 0;
                const finalUnitPrice = Math.max(0, unitPrice - discountPerUnit);
                const finalProductAmount = finalUnitPrice * quantity;

                // Calculate delivery cost (only for STANDARD delivery)
                const deliveryMethod = orderData.delivery?.method || 'STANDARD';
                const deliveryCost = (deliveryMethod === 'STANDARD' && !hero.freeDelivery) ? (hero.deliveryPrice || 0) : 0;

                const finalAmount = finalProductAmount + deliveryCost;

                const paymentLink = isDiscountActive && discount.paymentLink
                    ? discount.paymentLink
                    : paymentOptions.paymentLink;

                // Build button configuration
                const buttons = {};
                if (paymentOptions.showPayButton) {
                    buttons.confirmButton = {
                        text: `💳 Pay ${currency} ${finalAmount.toLocaleString('en-LK')} Now`,
                        value: 'pay'
                    };
                }
                if (paymentOptions.showSkipButton) {
                    buttons.cancelButton = {
                        text: "Skip - Pay Later 📞",
                        value: 'skip'
                    };
                }

                // Construct HTML for the popup
                const isPublicOrder = !orderData.isResellerOrder;
                const trackingUrl = `https://merchm.store/track/${docRef.id}`;

                let popupHtml = `
                    <div style="text-align: left;">
                        <p style="font-size: 1.05rem; margin-bottom: 12px;">
                            Your order has been received! 🎉
                        </p>
                        <div style="font-size: 0.9rem; background: #f3f4f6; padding: 15px; border-radius: 8px;">
                            <p><strong>Order ID:</strong> ${docRef.id.substring(0, 8)}</p>
                            <p><strong>Product:</strong> ${product.name}${quantity > 1 ? ` × ${quantity}` : ''}</p>
                `;

                if (isDiscountActive) {
                    popupHtml += `
                            <div style="margin-top: 8px; padding-top: 8px; border-top: 1px dashed #ccc;">
                                <p style="color: #666; text-decoration: line-through; font-size: 0.85rem;">
                                    Original: ${currency} ${(baseAmount + deliveryCost).toLocaleString('en-LK')}
                                </p>
                                <p style="color: #E85D44; font-weight: bold; font-size: 1.1rem;">
                                    Deal Price: ${currency} ${finalAmount.toLocaleString('en-LK')}
                                </p>
                                ${deliveryCost > 0 ? `<p style="font-size: 0.8rem; color: #666;">(Includes ${currency} ${deliveryCost.toLocaleString()} Delivery)</p>` : ''}
                                ${discount.reason ? `<p style="color: #2563eb; font-size: 0.85rem; margin-top: 4px;">🎁 ${discount.reason}</p>` : ''}
                            </div>
                    `;
                } else {
                    popupHtml += `
                            <p><strong>Total:</strong> ${currency} ${finalAmount.toLocaleString('en-LK')}</p>
                            ${deliveryCost > 0 ? `<p style="font-size: 0.8rem; color: #666;">(Includes ${currency} ${deliveryCost.toLocaleString()} Delivery)</p>` : ''}
                    `;
                }

                popupHtml += `
                        </div>
                        ${isPublicOrder ? `
                        <div style="background: #dbeafe; border-left: 4px solid #3b82f6; padding: 12px; margin-top: 15px;">
                            <p style="font-size: 0.9rem; color: #1e40af; margin: 0;">
                                📱 Check your WhatsApp for the tracking link
                            </p>
                        </div>
                        <div style="margin-top: 15px;">
                            <p style="font-size: 0.95rem; font-weight: 600; margin-bottom: 8px;">Next Steps:</p>
                            <ul style="font-size: 0.9rem; color: #555; margin-left: 20px; margin-bottom: 15px;">
                                <li>✅ Select your template design</li>
                                <li>📸 Upload your photos</li>
                                <li>💳 Submit payment proof</li>
                            </ul>
                            <a
                                href="${trackingUrl}"
                                style="display: block; padding: 12px; background: #10b981; color: white; text-decoration: none; border-radius: 8px; font-weight: 600; text-align: center; margin-bottom: 15px;"
                            >
                                📦 Go to Tracking Page
                            </a>
                        </div>
                        ` : ''}
                        <p style="font-size: 0.95rem; color: #555; margin-top: 15px;">
                            Choose your preferred payment method below 👇
                        </p>
                    </div>
                `;

                const result = await window.Swal.fire({
                    title: "✅ Order Placed Successfully!",
                    html: popupHtml,
                    icon: "success",
                    imageHeight: 180,
                    showCancelButton: !!buttons.cancelButton,
                    showConfirmButton: !!buttons.confirmButton,
                    showDenyButton: true,
                    denyButtonText: '➕ Place Another Order',
                    denyButtonColor: '#10b981',
                    confirmButtonText: buttons.confirmButton?.text || '',
                    cancelButtonText: buttons.cancelButton?.text || '',
                    confirmButtonColor: "#E85D44",
                    cancelButtonColor: "#6B7280",
                    backdrop: `rgba(0, 0, 0, 0.4)`,
                    allowOutsideClick: false,
                });

                if (result.isDenied) {
                    window.Swal.fire({
                        title: 'Success!',
                        text: 'You can now place your next order.',
                        icon: 'success',
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 3000
                    });
                    return; // Retain OrderForm
                }

                setShowOrderPopup(false);

                if (result.isConfirmed && paymentLink) {
                    // Redirect to custom payment link
                    window.location.href = paymentLink;
                } else if (result.isConfirmed) {
                    // Default: Show success message
                    window.Swal.fire({
                        title: 'Thank you!',
                        text: 'We will contact you via WhatsApp with payment details.',
                        icon: 'success',
                        confirmButtonText: 'Okay'
                    });
                } else {
                    // User chose "Skip - Pay Later" or dismissed
                    window.Swal.fire({
                        title: 'Order Confirmed!',
                        text: 'We will contact you on WhatsApp to confirm your order.',
                        icon: 'info',
                        confirmButtonText: 'Okay'
                    });
                }
            } else {
                // Payment options disabled - show simple WhatsApp confirmation
                const orderQty = orderData.priceAtOrder?.quantity || 1;
                const isPublicOrder = !orderData.isResellerOrder;
                const trackingUrl = `https://merchm.store/track/${docRef.id}`;

                const result = await window.Swal.fire({
                    title: "✅ Order Placed Successfully!",
                    html: `
                        <div style="text-align: left;">
                            <p style="font-size: 1.05rem; margin-bottom: 12px;">
                                Your order has been received! 🎉
                            </p>
                            <div style="font-size: 0.9rem; background: #f3f4f6; padding: 15px; border-radius: 8px;">
                                <p><strong>Order ID:</strong> ${docRef.id.substring(0, 8)}</p>
                                <p><strong>Product:</strong> ${product.name}${orderQty > 1 ? ` × ${orderQty}` : ''}</p>
                            </div>
                            ${isPublicOrder ? `
                            <div style="background: #dbeafe; border-left: 4px solid #3b82f6; padding: 12px; margin-top: 15px;">
                                <p style="font-size: 0.9rem; color: #1e40af; margin: 0;">
                                    📱 Check your WhatsApp for the tracking link
                                </p>
                            </div>
                            <div style="margin-top: 15px;">
                                <p style="font-size: 0.95rem; font-weight: 600; margin-bottom: 8px;">Next Steps:</p>
                                <ul style="font-size: 0.9rem; color: #555; margin-left: 20px; margin-bottom: 15px;">
                                    <li>✅ Select your template design</li>
                                    <li>📸 Upload your photos</li>
                                    <li>💳 Submit payment proof</li>
                                </ul>
                                <a
                                    href="${trackingUrl}"
                                    style="display: block; padding: 12px; background: #10b981; color: white; text-decoration: none; border-radius: 8px; font-weight: 600; text-align: center; margin-bottom: 15px;"
                                >
                                    📦 Go to Tracking Page
                                </a>
                            </div>
                            ` : `
                            <p style="font-size: 0.95rem; color: #555; margin-top: 15px;">
                                We will contact you on WhatsApp to confirm your order. 📞
                            </p>
                            `}
                        </div>
                    `,
                    icon: "success",
                    imageHeight: 180,
                    showDenyButton: true,
                    denyButtonText: '➕ Place Another',
                    denyButtonColor: '#10b981',
                    confirmButtonText: isPublicOrder ? "Got it!" : "Continue on WhatsApp",
                    confirmButtonColor: isPublicOrder ? "#E85D44" : "#25D366",
                    backdrop: `rgba(0, 0, 0, 0.4)`,
                    allowOutsideClick: false,
                });

                if (result.isDenied) {
                    window.Swal.fire({
                        title: 'Success!',
                        text: 'You can now place your next order.',
                        icon: 'success',
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 3000
                    });
                    return; // Retain OrderForm
                }

                setShowOrderPopup(false);

                // Open WhatsApp only for reseller orders
                if (!isPublicOrder && result.isConfirmed) {
                    const whatsappMsg = encodeURIComponent(`Hi! I just placed an order for ${product.name}${orderQty > 1 ? ` (qty: ${orderQty})` : ''}. Order ID: ${docRef.id.substring(0, 8)}`);
                    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMsg}`, '_blank');
                }
            }
        } catch (error) {
            console.error('Error placing order:', error);
            window.Swal.fire({
                title: 'Error',
                text: 'Failed to place order. Please try again.',
                icon: 'error',
                confirmButtonText: 'Okay'
            });
        }
    };

    return (
        <main className="bg-white text-gray-900">
            <Helmet>
                <title>{product.metaTitle || product.name} | Merch Masters LK</title>
                <meta name="description" content={product.metaDescription || product.shortDescription} />
            </Helmet>

            {/* Hero Section - Optimized Layout */}
            <section className="relative bg-gradient-to-br from-[#E85D44] to-[#ff5c4d] pt-24 md:pt-28 pb-16 md:pb-20 px-4 md:px-6 overflow-hidden">
                <div className="max-w-screen-xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                        {/* Left Content */}
                        <div className="space-y-6 text-white order-2 lg:order-1">
                            {/* Title Block */}
                            <div className="space-y-3">
                                <p className="text-xs md:text-sm uppercase tracking-[0.2em] md:tracking-[0.3em] text-white/80">
                                    {hero.slogan1 || 'ILLUMINATE YOUR MEMORIES'}
                                </p>
                                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                                    {product.name}
                                </h1>
                                <p className="text-base md:text-lg text-white/90 leading-relaxed max-w-lg">
                                    {hero.slogan2 || product.shortDescription}
                                </p>
                            </div>

                            {/* Feature Tags */}
                            {hero.tags && hero.tags.length > 0 && (
                                <div className="flex flex-wrap gap-2 text-xs md:text-sm">
                                    {hero.tags.map((tag, idx) => (
                                        <span key={idx} className="bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full">{tag}</span>
                                    ))}
                                </div>
                            )}

                            {/* Price Block - Proper hierarchy: ONE big number */}
                            <div className="space-y-1.5">
                                {/* Line 1: Primary Price + Badges */}
                                <div className="flex items-baseline gap-3 flex-wrap">
                                    <span className="text-4xl sm:text-5xl font-bold">
                                        {(() => {
                                            // Variant Price Logic
                                            const enabledSizes = (product.sizes || []).filter(s => s.enabled);

                                            if (enabledSizes.length > 0) {
                                                // Calculate Min/Max from variants
                                                const prices = enabledSizes.map(s => s.priceDelta || basePrice);
                                                const minPrice = Math.min(...prices);
                                                const maxPrice = Math.max(...prices);

                                                // Apply discount if active (assuming discount is fixed amount per unit)
                                                // Note: If discount is percentage, logic varies. Assuming fixed amount for now based on code (discount.amount).
                                                const discountAmount = isDiscountActive ? (discount.amount || 0) : 0;
                                                const effectiveMin = Math.max(0, minPrice - discountAmount);
                                                const effectiveMax = Math.max(0, maxPrice - discountAmount);

                                                if (effectiveMin !== effectiveMax) {
                                                    return `${product.priceCurrency || 'Rs.'} ${effectiveMin.toLocaleString()} - ${effectiveMax.toLocaleString()}`;
                                                } else {
                                                    return `${product.priceCurrency || 'Rs.'} ${effectiveMin.toLocaleString()}`;
                                                }
                                            }

                                            // Default single price
                                            return `${product.priceCurrency || 'Rs.'} ${effectivePrice.toLocaleString()}`;
                                        })()}
                                    </span>
                                    {/* Discount badge - only show % */}
                                    {isDiscountActive && (
                                        <span className="bg-green-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                                            Save {Math.round(((basePrice - effectivePrice) / basePrice) * 100)}%
                                        </span>
                                    )}
                                    {/* Free Delivery badge */}
                                    {hero.freeDelivery && (
                                        <span className="bg-white/20 text-white text-xs font-medium px-2.5 py-1 rounded-full">
                                            Free Delivery
                                        </span>
                                    )}
                                </div>

                                {/* Line 2: Secondary row - Old Price + Delivery (muted) */}
                                {(isDiscountActive || (!hero.freeDelivery && hero.deliveryPrice)) && (
                                    <div className="flex items-center gap-2 text-sm text-white/60 flex-wrap">
                                        {isDiscountActive && (
                                            <span className="line-through">
                                                {product.priceCurrency || 'Rs.'} {basePrice.toLocaleString()}
                                            </span>
                                        )}
                                        {isDiscountActive && !hero.freeDelivery && hero.deliveryPrice && (
                                            <span>•</span>
                                        )}
                                        {!hero.freeDelivery && hero.deliveryPrice && (
                                            <span>+ {product.priceCurrency || 'Rs.'} {hero.deliveryPrice} Delivery</span>
                                        )}
                                    </div>
                                )}

                                {/* Promo pill - visible badge under price */}
                                {isDiscountActive && discount.reason && (
                                    <span className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm text-white text-sm font-medium px-3 py-1.5 rounded-full w-fit">
                                        <span>💕</span>
                                        <span>{discount.reason}</span>
                                    </span>
                                )}

                                {/* Line 3: KOKO Installment */}
                                {(() => {
                                    const kokoDeliveryAmount = hero.freeDelivery ? 0 : (hero.deliveryPrice || 0);

                                    // Helper function to calculate installment
                                    const calcInstallment = (price) => {
                                        const total = (price + kokoDeliveryAmount) * 1.13;
                                        return Math.ceil(total / 3);
                                    };

                                    // Variant Logic
                                    const enabledSizes = (product.sizes || []).filter(s => s.enabled);
                                    let installmentText = '';

                                    if (enabledSizes.length > 0) {
                                        // Calculate Min/Max installments
                                        const prices = enabledSizes.map(s => s.priceDelta || basePrice);
                                        const minPrice = Math.min(...prices);
                                        const maxPrice = Math.max(...prices);

                                        // Apply discount if active
                                        const discountAmount = isDiscountActive ? (discount.amount || 0) : 0;
                                        const effectiveMin = Math.max(0, minPrice - discountAmount);
                                        const effectiveMax = Math.max(0, maxPrice - discountAmount);

                                        const minInstallment = calcInstallment(effectiveMin);
                                        const maxInstallment = calcInstallment(effectiveMax);

                                        if (minInstallment !== maxInstallment) {
                                            installmentText = `${product.priceCurrency || 'Rs.'} ${minInstallment.toLocaleString()} - ${maxInstallment.toLocaleString()}`;
                                        } else {
                                            installmentText = `${product.priceCurrency || 'Rs.'} ${minInstallment.toLocaleString()}`;
                                        }
                                    } else {
                                        // Single Price Logic
                                        const installment = calcInstallment(effectivePrice);
                                        installmentText = `${product.priceCurrency || 'Rs.'} ${installment.toLocaleString()}`;
                                    }

                                    return (
                                        <p className="text-xl sm:text-2xl text-white/90 flex items-center gap-2 flex-wrap pt-2 font-medium">
                                            <span>or 3 × {installmentText} with</span>
                                            <img
                                                src="/images/koko_brand-1024x341.png"
                                                alt="KOKO"
                                                className="h-8 sm:h-10 w-auto inline-block"
                                            />
                                        </p>
                                    );
                                })()}
                            </div>

                            {/* CTA Stack - Clear hierarchy */}
                            <div className="space-y-3 pt-2">
                                {/* Primary CTA - Order Now (Title Case, premium feel) */}
                                {product.enableDirectOrder ? (
                                    <button
                                        onClick={handleOrderNowClick}
                                        className="w-full sm:w-auto px-10 py-4 bg-white text-[#E85D44] hover:bg-gray-50 font-bold text-base rounded-lg transition-all duration-300 shadow-xl hover:shadow-2xl"
                                    >
                                        Start My Order →
                                    </button>
                                ) : (
                                    hero.showStartDesign && hero.startDesignLink && (
                                        <a
                                            href={hero.startDesignLink}
                                            className="inline-block w-full sm:w-auto px-10 py-4 bg-white text-[#E85D44] hover:bg-gray-50 font-bold text-base rounded-lg transition-all duration-300 shadow-xl hover:shadow-2xl text-center"
                                        >
                                            Create My Lamp →
                                        </a>
                                    )
                                )}

                                {/* 3-Step Micro Flow - Reduces anxiety */}
                                <p className="text-xs text-white/80 flex items-center gap-2 flex-wrap">
                                    <span className="flex items-center gap-1"><span className="bg-white/20 rounded-full w-4 h-4 flex items-center justify-center text-[10px]">1</span> Upload photos</span>
                                    <span className="text-white/40">→</span>
                                    <span className="flex items-center gap-1"><span className="bg-white/20 rounded-full w-4 h-4 flex items-center justify-center text-[10px]">2</span> Approve preview</span>
                                    <span className="text-white/40">→</span>
                                    <span className="flex items-center gap-1"><span className="bg-white/20 rounded-full w-4 h-4 flex items-center justify-center text-[10px]">3</span> Delivered in 3-5 days</span>
                                </p>

                                {/* Secondary CTA - WhatsApp */}
                                {hero.showWhatsapp && (
                                    <a
                                        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappDesignMessage}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 border border-white/50 hover:border-white hover:bg-white/10 text-white text-sm font-medium rounded-lg transition-all duration-300"
                                    >
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                        </svg>
                                        Need help? WhatsApp us
                                    </a>
                                )}

                                {/* Payments info */}
                                <p className="text-sm text-white/70 flex items-center gap-3 pt-1">
                                    <span>Payments:</span>
                                    <img src="/images/payhere-logo.png" alt="PayHere" className="h-8 w-auto opacity-80" />
                                    <span>•</span>
                                    <img src="/images/koko_brand-1024x341.png" alt="KOKO" className="h-8 w-auto opacity-80" />
                                </p>
                            </div>

                            {hero.showWhatsapp && (
                                <p className="text-xs text-white/40">*Terms & Conditions Apply</p>
                            )}
                        </div>

                        {/* Right Images - Carousel */}
                        {hero.images && hero.images.length > 0 && (
                            <div className="order-1 lg:order-2">
                                <HeroCarousel images={hero.images} />
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Quick Spec Strip */}
            {specs.length > 0 && (
                <section className="bg-[#FFF5F2] py-10 md:py-16 px-4 md:px-6">
                    <div className="max-w-screen-xl mx-auto">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
                            {specs.map((spec, idx) => (
                                <div key={idx} className="text-center p-3 md:p-0">
                                    <div className="w-12 h-12 md:w-16 md:h-16 bg-[#E85D44]/10 rounded-full flex items-center justify-center mx-auto mb-2 md:mb-4">
                                        {getSpecIcon(spec.icon)}
                                    </div>
                                    <h3 className="font-bold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">{spec.heading}</h3>
                                    <p className="text-xs md:text-sm text-gray-600 line-clamp-2">{spec.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Dynamic Content Sections */}
            {contentSections.map((section, idx) => {
                const isLeft = section.type === 'left';
                const bgClass = idx % 2 === 0 ? 'bg-white' : 'bg-[#FFF5F2]';

                return (
                    <section key={section.id} className={`py-12 md:py-20 px-4 md:px-6 ${bgClass}`}>
                        <div className="max-w-screen-xl mx-auto">
                            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center ${isLeft ? '' : 'lg:grid-flow-dense'}`}>
                                <div className={`space-y-4 md:space-y-6 ${isLeft ? '' : 'lg:col-start-2'}`}>
                                    <p className="text-xs md:text-sm uppercase tracking-[0.2em] md:tracking-[0.3em] text-[#E85D44]">
                                        {section.slogan}
                                    </p>
                                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif text-gray-900 leading-tight">
                                        {section.heading}
                                    </h2>
                                    <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                                        {section.description1}
                                    </p>
                                    {section.description2 && (
                                        <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                                            {section.description2}
                                        </p>
                                    )}
                                    {section.notes && section.notes.length > 0 && section.notes.map((note, noteIdx) => {
                                        const noteColors = {
                                            info: 'bg-blue-50 border-blue-400',
                                            warning: 'bg-[#FFF5F2] border-[#E85D44]',
                                            danger: 'bg-red-50 border-red-400'
                                        };
                                        return (
                                            <div key={noteIdx} className={`${noteColors[note.type] || noteColors.info} border-l-4 p-4 rounded`}>
                                                <p className="text-sm text-gray-600">
                                                    <strong>{note.type === 'warning' ? 'Note:' : note.type === 'danger' ? 'Important:' : 'Info:'}</strong> {note.text}
                                                </p>
                                            </div>
                                        );
                                    })}
                                    {section.showLink && section.linkUrl && (
                                        <div className="pt-2 md:pt-4">
                                            <a
                                                href={section.linkUrl}
                                                className="inline-block bg-transparent border-2 border-gray-300 hover:border-[#E85D44] hover:bg-[#E85D44] text-gray-700 hover:text-white font-semibold text-xs md:text-sm uppercase tracking-[0.15em] md:tracking-[0.2em] px-6 md:px-8 py-2.5 md:py-3 transition-all duration-300"
                                            >
                                                {section.linkText || 'Learn More'}
                                            </a>
                                        </div>
                                    )}
                                </div>
                                {section.image && (
                                    <div className={`rounded-lg overflow-hidden ${isLeft ? '' : 'lg:col-start-1 lg:row-start-1'}`}>
                                        <img src={section.image.url} alt={section.image.alt || section.heading} className="w-full h-full object-cover" loading="lazy" />
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                );
            })}

            {/* Process Section */}
            {showProcess && <ProcessSection />}

            {/* Guidelines Section */}
            {showGuidelines && <ImageGuidelinesSection />}

            {/* Example Images */}
            {product.exampleImages && product.exampleImages.length > 0 && (
                <section className="py-12 md:py-20 px-4 md:px-6 bg-[#FFFFFF]">
                    <div className="max-w-screen-xl mx-auto">
                        <div className="text-center mb-8 md:mb-12">
                            <p className="text-xs md:text-sm uppercase tracking-[0.2em] md:tracking-[0.3em] text-[#E85D44] mb-2 md:mb-4">
                                SEE THE REAL RESULTS
                            </p>
                            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-serif text-gray-900 mb-4 md:mb-6">
                                Product Snaps
                            </h2>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                            {product.exampleImages.map((img, index) => (
                                <div
                                    key={index}
                                    className="aspect-square rounded-lg md:rounded-xl overflow-hidden bg-gray-100 animate-fade-in-left"
                                    style={{ animationDelay: `${index * 100}ms` }}
                                >
                                    <img src={img.url} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )
            }

            {/* Final CTA Section */}
            <section className="py-12 md:py-20 px-4 md:px-6 bg-gradient-to-br from-[#E85D44] to-[#ff5c4d] text-white text-center">
                <div className="max-w-screen-xl mx-auto">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-4 md:mb-6">
                        Ready to Create Your Masterpiece?
                    </h2>
                    <p className="text-base md:text-xl text-white/90 mb-6 md:mb-10 max-w-3xl mx-auto">
                        Don't wait! Bring your ideas to life with our high-quality custom products.
                        Start designing now or talk to our team for personalized assistance.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-6">
                        {product.enableDirectOrder ? (
                            <button
                                onClick={handleOrderNowClick}
                                className="w-full sm:w-auto inline-block px-6 md:px-10 py-3.5 md:py-4 bg-white text-[#E85D44] hover:bg-gray-100 font-semibold text-sm uppercase tracking-[0.15em] md:tracking-[0.2em] rounded-lg transition-all duration-300 shadow-xl hover:shadow-2xl"
                            >
                                Place your order now
                            </button>
                        ) : (
                            hero.showStartDesign && hero.startDesignLink && (
                                <a
                                    href={hero.startDesignLink}
                                    className="w-full sm:w-auto inline-block px-6 md:px-10 py-3.5 md:py-4 bg-white text-[#E85D44] hover:bg-gray-100 font-semibold text-sm uppercase tracking-[0.15em] md:tracking-[0.2em] rounded-lg transition-all duration-300 shadow-xl hover:shadow-2xl text-center"
                                >
                                    Start Designing Now
                                </a>
                            )
                        )}
                        {hero.showWhatsapp && (
                            <a
                                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappDesignMessage}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 md:gap-3 px-6 md:px-8 py-3.5 md:py-4 bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold text-sm uppercase tracking-[0.1em] md:tracking-[0.15em] rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                </svg>
                                Talk to Our Design Team
                            </a>
                        )}
                    </div>
                </div>
            </section>

            {/* Order Popup */}
            {showOrderPopup && (
                <OrderForm
                    productName={product.name}
                    pricing={{
                        basePrice: basePrice, // Use original price, not effectivePrice
                        originalPrice: null, // Not used when discount is passed separately
                        discountReason: isDiscountActive ? discount.reason : null,
                        discount: isDiscountActive ? discount.amount : 0, // Pass discount amount
                        currency: product.priceCurrency || 'LKR',
                        listType: 'RETAIL',
                        deliveryIncluded: hero.freeDelivery || false,
                        deliveryPrice: hero.freeDelivery ? 0 : (hero.deliveryPrice || 0)
                    }}
                    quantitySettings={product.variableQuantity || { enabled: false, minQty: 1, maxQty: 10 }}
                    templates={product.templates || { enabled: false, items: [] }}
                    sizes={product.sizes || []}
                    paymentOptions={product.paymentOptions || {}}
                    onSubmit={handleOrderSubmit}
                    onCancel={() => setShowOrderPopup(false)}
                />
            )}


        </main >
    );
};

export default ProductPage;
